<template>
    <div class="analytics-hybrid-trend-shift-chart w-full">
        <div v-if="isLoading" class="text-center py-8">
            <div class="inline-flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="text-sm text-gray-600">Loading trend shift chart…</span>
            </div>
        </div>

        <div v-else-if="errorMessage" class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">{{ errorTitle }}</h3>
            <p class="mt-1 text-sm text-gray-500">{{ errorMessage }}</p>
            <button type="button" @click="loadChartData" class="mt-4 text-sm text-blue-600 hover:text-blue-500">
                Try Again
            </button>
        </div>

        <div class="space-y-4">
            <div class="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div
                    ref="chartContainer"
                    :style="{ ...graphHeight, visibility: isLoading || errorMessage ? 'hidden' : 'visible' }"
                    class="w-full"
                ></div>
                <div class="absolute top-2 left-2 bg-white bg-opacity-90 px-3 py-1 rounded-md shadow-sm pointer-events-none">
                    <span class="text-sm font-semibold text-gray-700">{{ chartOverlayTitle }}</span>
                </div>
            </div>

            <template v-if="!isLoading && !errorMessage">
                <div v-if="!insightsSimplified" class="flex flex-wrap gap-4 text-xs text-gray-600 px-1">
                    <span class="inline-flex items-center gap-1.5">
                        <span class="inline-block w-2.5 h-2.5 rounded-full" style="background:#2563eb"></span> ML-only
                    </span>
                    <span class="inline-flex items-center gap-1.5">
                        <span class="inline-block w-2.5 h-2.5 rounded-full" style="background:#16a34a"></span> Statistical
                    </span>
                    <span class="inline-flex items-center gap-1.5">
                        <span class="inline-block w-2.5 h-2.5 rounded-full" style="background:#d97706"></span> Both
                    </span>
                </div>

                <div v-if="!changePoints.length" class="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                    No change points in the latest hybrid trend shift run. The time series still reflects daily averages and the moving average for context.
                </div>

                <div v-if="hybridRecordedAt && !insightsSimplified" class="text-xs text-gray-500">
                    Hybrid analytics recorded {{ hybridRecordedAt }}
                </div>

                <div v-if="!insightsSimplified" class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <h5 class="text-sm font-semibold text-gray-900 mb-3">Baseline vs recent trend</h5>
                    <div v-if="trendComparison.insufficient_baseline_data" class="text-sm text-gray-600">
                        Not enough daily history for a 30-day baseline and 7-day recent comparison (need at least 37 days with readings).
                    </div>
                    <div v-else class="space-y-4">
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                                <div class="text-xs font-medium uppercase text-gray-500">Baseline slope <span class="normal-case text-gray-400">(30 d)</span></div>
                                <div class="text-lg font-mono text-indigo-700">{{ formatSlope(trendComparison.baseline_slope) }}</div>
                            </div>
                            <div>
                                <div class="text-xs font-medium uppercase text-gray-500">Recent slope <span class="normal-case text-gray-400">(7 d)</span></div>
                                <div class="text-lg font-mono text-emerald-700">{{ formatSlope(trendComparison.recent_slope) }}</div>
                            </div>
                        </div>
                        <div>
                            <div class="text-xs font-medium text-gray-500 mb-1">Difference (recent − baseline)</div>
                            <div class="flex items-center gap-3">
                                <div class="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
                                    <div
                                        class="h-full bg-amber-500 transition-all"
                                        :style="{ width: slopeDiffMagnitudePercent }"
                                    ></div>
                                </div>
                                <span class="font-mono text-sm text-gray-800 whitespace-nowrap">{{ formatSlope(trendComparison.slope_difference) }}</span>
                            </div>
                            <p v-if="trendComparison.is_statistically_significant" class="mt-2 text-xs text-amber-800">
                                Statistically significant shift (two-tailed p={{ formatP(trendComparison.p_value_two_tailed) }}).
                            </p>
                            <p v-else class="mt-2 text-xs text-gray-500">
                                No significant slope shift at the default α threshold.
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { SonicuCoreMixin } from '@/mixins/SonicuCoreMixin';
import { amchartsMixin } from '@/mixins/amchartsMixin';

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

export default {
    name: 'AnalyticsHybridTrendShiftChart',
    mixins: [SonicuCoreMixin, amchartsMixin],
    props: {
        meterId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            isLoading: true,
            errorMessage: '',
            errorTitle: 'Chart Error',
            chartData: [],
            changePoints: [],
            mlForecastBand: [],
            trendComparison: { insufficient_baseline_data: true },
            hybridRecordedAt: null,
            isLoadingData: false,
            insightsSimplified: false,
        };
    },
    created() {
        Object.defineProperty(this, 'root', {
            value: null,
            writable: true,
            enumerable: false,
            configurable: true,
        });
        this.initializeAM5Charts();
    },
    mounted() {
        this.bindInsightsMode();
        this.$nextTick(() => {
            this.initializeChart();
            this.loadChartData();
        });
    },
    beforeUnmount() {
        if (this._insightsModeHandler) {
            window.removeEventListener('advanced-analytics-insights-mode', this._insightsModeHandler);
        }
        this.clearChangePointRanges();
        if (this.root) {
            this.root.dispose();
            this.root = null;
        }
    },
    methods: {
        bindInsightsMode() {
            const apply = () => {
                this.insightsSimplified =
                    typeof window !== 'undefined' && window.__ADVANCED_ANALYTICS_INSIGHTS_MODE === 'simple';
            };
            apply();
            this._insightsModeHandler = () => apply();
            window.addEventListener('advanced-analytics-insights-mode', this._insightsModeHandler);
        },
        initializeChart() {
            this.root = am5.Root.new(this.$refs.chartContainer);
            this.root.setThemes([am5themes_Animated.new(this.root)]);

            const chart = this.root.container.children.push(
                am5xy.XYChart.new(this.root, {
                    panX: true,
                    panY: false,
                    wheelX: 'panX',
                    wheelY: 'zoomX',
                    pinchZoomX: true,
                    paddingTop: 28,
                    paddingRight: 20,
                    paddingBottom: 20,
                    paddingLeft: 20,
                }),
            );

            const xAxis = chart.xAxes.push(
                am5xy.DateAxis.new(this.root, {
                    baseInterval: { timeUnit: 'day', count: 1 },
                    renderer: am5xy.AxisRendererX.new(this.root, { minGridDistance: 50 }),
                    tooltip: am5.Tooltip.new(this.root, {}),
                }),
            );
            xAxis.get('renderer').labels.template.setAll({ fontSize: 12 });

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(this.root, {
                    renderer: am5xy.AxisRendererY.new(this.root, { minWidth: 50 }),
                }),
            );
            yAxis.get('renderer').labels.template.adapters.add('text', (text) => {
                if (text !== undefined && text !== null) {
                    return parseFloat(text).toFixed(4);
                }
                return text;
            });

            const bandSeries = chart.series.push(
                am5xy.LineSeries.new(this.root, {
                    name: 'ML forecast band',
                    xAxis,
                    yAxis,
                    valueYField: 'high',
                    openValueYField: 'low',
                    valueXField: 'date_ms',
                    stroke: am5.color('#93c5fd'),
                    fill: am5.color('#93c5fd'),
                    tooltip: am5.Tooltip.new(this.root, {
                        labelText: 'Band: {openValueY.formatNumber("#.####")} – {valueY.formatNumber("#.####")}',
                    }),
                }),
            );
            bandSeries.fills.template.setAll({ fillOpacity: 0.22, visible: true });
            bandSeries.strokes.template.setAll({ strokeOpacity: 0 });

            const fcCenterSeries = chart.series.push(
                am5xy.LineSeries.new(this.root, {
                    name: 'ML forecast (center)',
                    xAxis,
                    yAxis,
                    valueYField: 'center',
                    valueXField: 'date_ms',
                    stroke: am5.color('#60a5fa'),
                    tooltip: am5.Tooltip.new(this.root, {
                        labelText: 'Forecast: {valueY.formatNumber("#.######")}',
                    }),
                }),
            );
            fcCenterSeries.strokes.template.setAll({ strokeWidth: 1, strokeDasharray: [4, 3] });

            const dailySeries = chart.series.push(
                am5xy.LineSeries.new(this.root, {
                    name: 'Daily average',
                    xAxis,
                    yAxis,
                    valueYField: 'daily_average',
                    valueXField: 'date',
                    stroke: am5.color('#10B981'),
                    tooltip: am5.Tooltip.new(this.root, {
                        labelText: 'Daily avg: {valueY.formatNumber("#.######")}',
                    }),
                }),
            );
            dailySeries.strokes.template.setAll({ strokeWidth: 1, strokeDasharray: [5, 5] });

            const maSeries = chart.series.push(
                am5xy.LineSeries.new(this.root, {
                    name: '30-day moving average',
                    xAxis,
                    yAxis,
                    valueYField: 'moving_average',
                    valueXField: 'date',
                    stroke: am5.color('#4F46E5'),
                    tooltip: am5.Tooltip.new(this.root, {
                        labelText: 'Moving avg: {valueY.formatNumber("#.######")}',
                    }),
                }),
            );
            maSeries.strokes.template.setAll({ strokeWidth: 2 });

            chart.set(
                'cursor',
                am5xy.XYCursor.new(this.root, {
                    behavior: 'zoomX',
                    xAxis,
                }),
            );
            chart.set('scrollbarX', am5.Scrollbar.new(this.root, { orientation: 'horizontal' }));

            const legend = chart.bottomAxesContainer.children.push(
                am5.Legend.new(this.root, {
                    centerX: am5.percent(50),
                    x: am5.percent(50),
                    paddingTop: 8,
                }),
            );
            legend.data.setAll(chart.series.values);

            this._chart = chart;
            this._xAxis = xAxis;
            this._bandSeries = bandSeries;
            this._fcCenterSeries = fcCenterSeries;
            this._dailySeries = dailySeries;
            this._maSeries = maSeries;
            this._cpAxisRanges = [];
        },

        clearChangePointRanges() {
            if (this._cpAxisRanges && this._xAxis) {
                this._cpAxisRanges.forEach((r) => {
                    try {
                        r.dispose();
                    } catch (e) {
                        /* noop */
                    }
                });
            }
            this._cpAxisRanges = [];
        },

        renderChangePointRanges(points) {
            this.clearChangePointRanges();
            if (!this._xAxis || !this.root || !points?.length) {
                return;
            }
            points.forEach((cp) => {
                const rangeDataItem = this._xAxis.makeDataItem({ value: cp.timestamp_ms });
                const axisRange = this._xAxis.createAxisRange(rangeDataItem);
                axisRange.get('grid').setAll({
                    stroke: am5.color(cp.color),
                    strokeWidth: 2,
                    strokeOpacity: 0.9,
                    location: 0,
                });
                const mag = cp.magnitude != null && Number.isFinite(cp.magnitude) ? Number(cp.magnitude).toFixed(4) : '—';
                const confPct =
                    cp.confidence != null && Number.isFinite(cp.confidence)
                        ? `${(Number(cp.confidence) * 100).toFixed(0)}%`
                        : '—';
                const tip = `[bold]${cp.source_label}[/]\n[fontSize:11px]Direction: ${cp.direction ?? '—'}\nMagnitude: ${mag}\nConfidence: ${confPct}\n${cp.timestamp_iso}[/]`;
                axisRange.get('grid').set(
                    'tooltip',
                    am5.Tooltip.new(this.root, {
                        labelText: tip,
                    }),
                );
                this._cpAxisRanges.push(axisRange);
            });
        },

        async loadChartData() {
            if (this.isLoadingData) {
                return;
            }
            this.isLoadingData = true;
            this.isLoading = true;
            this.errorMessage = '';

            try {
                const response = await axios.get(
                    this.company.buildCompanyApiInternalRoute('analytics-meter') + '/' + this.meterId,
                    { params: { metric: 'hybrid_trend_shift' } },
                );

                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    const rows = response.data.data.map((item) => ({
                        date: new Date(item.full_date || item.timestamp).getTime(),
                        moving_average: parseFloat(item.raw_moving_avg ?? item.moving_average),
                        daily_average: parseFloat(item.raw_daily_avg ?? item.daily_average ?? item.moving_average),
                    }));

                    this.chartData = rows;
                    this.changePoints = response.data.change_points || [];
                    this.mlForecastBand = response.data.ml_forecast_band || [];
                    this.trendComparison = response.data.trend_comparison || { insufficient_baseline_data: true };
                    this.hybridRecordedAt = response.data.hybrid_analytics_recorded_at || null;

                    this._maSeries.data.setAll(rows);
                    this._dailySeries.data.setAll(rows);

                    if (this.mlForecastBand.length) {
                        this._bandSeries.data.setAll(this.mlForecastBand);
                        this._fcCenterSeries.data.setAll(this.mlForecastBand);
                    } else {
                        this._bandSeries.data.setAll([]);
                        this._fcCenterSeries.data.setAll([]);
                    }

                    this.renderChangePointRanges(this.changePoints);
                    this.isLoading = false;
                } else {
                    this.handleError(
                        'No Chart Data Available',
                        response.data.message || 'No data available for the selected period',
                    );
                }
            } catch (error) {
                this.handleError(
                    'Failed to Load Chart',
                    error.response?.data?.message || error.message || 'Failed to load chart data',
                );
            } finally {
                this.isLoadingData = false;
            }
        },

        handleError(title, message) {
            this.isLoading = false;
            this.isLoadingData = false;
            this.errorTitle = title;
            this.errorMessage = message;
            this.chartData = [];
            this.changePoints = [];
            this.clearChangePointRanges();
        },

        formatSlope(s) {
            if (s == null || !Number.isFinite(Number(s))) {
                return '—';
            }
            const n = Number(s);
            const a = Math.abs(n);
            if (a > 0 && a < 0.0001) {
                return n.toExponential(2);
            }
            return n.toFixed(4);
        },

        formatP(p) {
            if (p == null || !Number.isFinite(Number(p))) {
                return '—';
            }
            return Number(p).toFixed(4);
        },

    },
    computed: {
        chartOverlayTitle() {
            return this.insightsSimplified ? 'Readings & shift markers' : 'Daily series & change points';
        },
        graphHeight() {
            let clientHeight = document.documentElement.clientHeight;
            clientHeight = Math.max(clientHeight - 400, 450);
            return { height: `${clientHeight}px`, width: '100%' };
        },
        slopeDiffMagnitudePercent() {
            const d = this.trendComparison.slope_difference;
            if (d == null || !Number.isFinite(Number(d))) {
                return '0%';
            }
            const diff = Number(d);
            const b = this.trendComparison.baseline_slope;
            const r = this.trendComparison.recent_slope;
            const mag = Math.max(Math.abs(b) || 0, Math.abs(r) || 0, Math.abs(diff) || 0, 1e-9);
            const pct = Math.min(100, (Math.abs(diff) / mag) * 100);
            return `${pct}%`;
        },
    },
};
</script>

<style scoped>
.analytics-hybrid-trend-shift-chart {
    width: 100%;
}
</style>
